import React from "react";

// Shared delegated-work status card.
// PARITY: this file must stay byte-identical between client (Cortex OS) and
// superadmin (Quantum OS).
//
// Presentational only: every fact comes from the serialized work request.
// Status is always shown as text (never color alone), and the card makes no
// activation/execution claims — a READY draft is "ready for review", nothing
// more.

const STATUS_PRESENTATION = {
  RECEIVED: { label: "Received", tone: "pending" },
  NEEDS_CLARIFICATION: { label: "Needs one detail", tone: "pending" },
  QUEUED: { label: "Queued", tone: "pending" },
  IN_PROGRESS: { label: "Drafting", tone: "active" },
  READY_FOR_REVIEW: { label: "Ready for review", tone: "done" },
  FAILED: { label: "Needs attention", tone: "danger" },
  CANCELLED: { label: "Cancelled", tone: "muted" },
};

const CANCELLABLE = new Set(["RECEIVED", "NEEDS_CLARIFICATION", "QUEUED"]);

function formatTime(value) {
  if (!value) return null;
  try {
    return new Date(value).toLocaleString();
  } catch {
    return null;
  }
}

export function EdenWorkStatusCard({ work, onCancel, onOpenDraft, onRetry, onClarify }) {
  const [clarificationText, setClarificationText] = React.useState("");
  if (!work || !work.id) return null;
  const presentation = STATUS_PRESENTATION[work.status] || { label: work.status, tone: "muted" };
  const started = formatTime(work.startedAt || work.createdAt);
  const updated = formatTime(work.completedAt || work.failedAt || work.cancelledAt || work.startedAt || work.createdAt);
  const clarificationQuestion = work.clarification?.questions?.[0]?.question || null;
  const isBusy = work.status === "QUEUED" || work.status === "IN_PROGRESS";

  return (
    <section
      className={`eden-work-status-card eden-work-status-card--${presentation.tone}`}
      aria-label={`Delegated work: ${work.opportunityName || "media plan draft"}`}
      aria-busy={isBusy}
    >
      <div className="eden-work-status-card-header">
        <span className="eden-work-status-card-name">
          {work.opportunityName || "Media plan draft"}
        </span>
        <span className={`eden-work-status-card-badge eden-work-status-card-badge--${presentation.tone}`} role="status">
          {presentation.label}
        </span>
      </div>
      <dl className="eden-work-status-card-facts">
        {work.advertiserName ? (
          <div className="eden-work-status-card-fact">
            <dt>Advertiser</dt>
            <dd>{work.advertiserName}</dd>
          </div>
        ) : null}
        {work.stage ? (
          <div className="eden-work-status-card-fact">
            <dt>Stage</dt>
            <dd>{String(work.stage).replace(/_/g, " ")}</dd>
          </div>
        ) : null}
        {started ? (
          <div className="eden-work-status-card-fact">
            <dt>Started</dt>
            <dd>{started}</dd>
          </div>
        ) : null}
        {updated ? (
          <div className="eden-work-status-card-fact">
            <dt>Last update</dt>
            <dd>{updated}</dd>
          </div>
        ) : null}
      </dl>

      {clarificationQuestion ? (
        <div className="eden-work-status-card-clarify">
          <label className="eden-work-status-card-clarification" htmlFor={`eden-work-clarify-${work.id}`}>
            {clarificationQuestion}
          </label>
          {typeof onClarify === "function" ? (
            <div className="eden-work-status-card-clarify-row">
              <input
                id={`eden-work-clarify-${work.id}`}
                className="eden-work-status-card-clarify-input"
                type="text"
                placeholder="e.g. 2026-09-04 through 2026-10-16"
                value={clarificationText}
                onChange={(event) => setClarificationText(event.target.value)}
              />
              <button
                type="button"
                className="eden-work-status-card-action eden-work-status-card-action--primary"
                disabled={!clarificationText.trim()}
                onClick={() => {
                  onClarify(work, clarificationText.trim());
                  setClarificationText("");
                }}
              >
                Send answer
              </button>
            </div>
          ) : null}
        </div>
      ) : null}

      {work.status === "FAILED" && work.errorMessage ? (
        <p className="eden-work-status-card-error">{work.errorMessage}</p>
      ) : null}

      <div className="eden-work-status-card-actions">
        {work.status === "READY_FOR_REVIEW" && work.reviewPath && typeof onOpenDraft === "function" ? (
          <button
            type="button"
            className="eden-work-status-card-action eden-work-status-card-action--primary"
            onClick={() => onOpenDraft(work)}
          >
            Open draft
          </button>
        ) : null}
        {work.status === "FAILED" && typeof onRetry === "function" ? (
          <button
            type="button"
            className="eden-work-status-card-action"
            onClick={() => onRetry(work)}
          >
            Retry safely
          </button>
        ) : null}
        {CANCELLABLE.has(work.status) && typeof onCancel === "function" ? (
          <button
            type="button"
            className="eden-work-status-card-action eden-work-status-card-action--quiet"
            onClick={() => onCancel(work)}
          >
            Cancel
          </button>
        ) : null}
      </div>
    </section>
  );
}

export default EdenWorkStatusCard;
